import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type ReactNode,
  type Ref,
} from 'react';
import { createPortal } from 'react-dom';
import { Box, Flex } from '@chakra-ui/react';
import { useGetResumesQuery } from '@/store/query/ats-contacts';
import { CandidateProfileCard } from '../candidateProfiles/CandidateProfileCard';
import {
  fromAtsResumes,
  type AtsResumeFile,
} from '../candidateProfiles/toResumeAttachments';
import type { CandidateSummary } from '../../../types';

// Bind these to the candidate name so clicking the name (not the whole card)
// opens the panel; the ref also anchors the panel to the thread.
export interface NameTriggerBind {
  ref: Ref<HTMLDivElement>;
  onClick: () => void;
}

const PANEL_WIDTH = 720;
const MARGIN = 16;
// Nudge the centered panel right so the clicked card's avatar + name stay visible.
const NUDGE_RIGHT = 150;
const PANEL_Z = 2100;

// Rect of the area the panel is centered within (the thread scroll area).
interface Bounds {
  top: number;
  left: number;
  width: number;
  height: number;
}

// The scrollable thread area (ThreadBody) sits below the widget header, so its
// rect is the right anchor — the widget's real bounds, not the viewport. Target
// it directly (the `role="log"` conversation region) so a trigger nested inside
// another scroll container — e.g. the data table's horizontal-scroll wrapper —
// still anchors to the thread, not that inner box (otherwise the backdrop only
// covers the table and clicks beside the panel don't close it). Falls back to
// the first scrollable ancestor, then the widget root, then the document.
const findThreadBounds = (el: HTMLElement): DOMRect => {
  const log = el.closest('[role="log"]') as HTMLElement | null;
  if (log) return log.getBoundingClientRect();
  let cur: HTMLElement | null = el.parentElement;
  while (cur) {
    const oy = getComputedStyle(cur).overflowY;
    if (oy === 'auto' || oy === 'scroll') return cur.getBoundingClientRect();
    cur = cur.parentElement;
  }
  const container = el.closest(
    '[data-test-id="thread-container"]'
  ) as HTMLElement | null;
  return (container ?? document.documentElement).getBoundingClientRect();
};

// A click overlay that previews the full candidate profile. The panel is
// centered — both horizontally and vertically — within the thread, like a
// modal, independent of which card was clicked. Opens on clicking the candidate
// name (via the render-prop bind), not the whole card; clicking the backdrop or
// pressing Escape closes it. Reuses the carousel's CandidateProfileCard (which
// self-fetches the contact by id) and feeds it the resume from the healthy host
// ATS endpoint. Everything only loads while the panel is open.
export const CandidateHoverProfile = ({
  candidate,
  children,
}: {
  candidate: CandidateSummary;
  children: (bind: NameTriggerBind) => ReactNode;
}) => {
  const triggerRef = useRef<HTMLDivElement>(null);
  const [bounds, setBounds] = useState<Bounds | null>(null);
  const open = bounds !== null;

  // Resumes come from the healthy host ATS endpoint (/contacts/:id/resumes),
  // not the flaky agentforge /profile call the card would otherwise use — so
  // the Resume section renders below Skills. Only fetched while the panel is
  // open. Passing it in also disables the card's own (flaky) resume fetch.
  const { data: atsResumes, isError: resumesError } = useGetResumesQuery(
    { contactId: candidate.id },
    { skip: !open }
  ) as { data?: AtsResumeFile[]; isError: boolean };
  const resumes = fromAtsResumes(atsResumes ?? []);
  // Loading = panel open but the resume hasn't resolved (or errored) yet. More
  // reliable than RTK's isLoading across the skip→active gap, and it keeps the
  // card's spinner up until we know resume-vs-details (no content flash).
  const resumesLoading = open && !resumesError && atsResumes === undefined;

  const close = useCallback(() => setBounds(null), []);
  const openNow = useCallback(() => {
    const el = triggerRef.current;
    if (!el) return;
    const r = findThreadBounds(el);
    setBounds({ top: r.top, left: r.left, width: r.width, height: r.height });
  }, []);

  // Escape closes the panel while it's open.
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') close();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, close]);

  return (
    <>
      {children({ ref: triggerRef, onClick: openNow })}
      {bounds &&
        createPortal(
          // Backdrop over the thread: a click anywhere outside the panel closes
          // it. The panel itself stops the click from bubbling to the backdrop.
          <Flex
            position="fixed"
            top={`${bounds.top}px`}
            left={`${bounds.left}px`}
            width={`${bounds.width}px`}
            height={`${bounds.height}px`}
            align="center"
            justify="center"
            p={`${MARGIN}px`}
            zIndex={PANEL_Z}
            onClick={close}
          >
            <Box
              width={`${Math.min(PANEL_WIDTH, bounds.width - MARGIN * 2)}px`}
              maxHeight="100%"
              transform={`translateX(${NUDGE_RIGHT}px)`}
              onClick={(e) => e.stopPropagation()}
              data-test-id={`agentforge-candidate-hover-panel-${candidate.id}`}
            >
              <CandidateProfileCard
                candidate={candidate}
                active
                resumes={resumes}
                resumesLoading={resumesLoading}
              />
            </Box>
          </Flex>,
          document.body
        )}
    </>
  );
};
